/* ==========================================================================
   Decoratieve video's

   Regelt vijf dingen die je niet in Liquid of CSS kan oplossen:

   1. Starten. De video staat bewust zonder autoplay in de markup. Met
      autoplay begint de browser al te spelen voor dit script (defer) iets
      kan zeggen, en dan beweegt er toch iets bij prefers-reduced-motion.
      Hier start hij pas als hij in beeld is en de bezoeker geen verminderde
      beweging gevraagd heeft. Zonder JavaScript blijft hij stil op het
      poster-beeld staan.

   2. prefers-reduced-motion. Dan speelt er niets vanzelf en blijft het
      poster-beeld staan. CSS kan een video verbergen maar niet stoppen,
      vandaar dat dit hier gebeurt.

   3. Pauzeren buiten beeld. Vijf video's in een mozaïek die allemaal blijven
      doorspelen kosten processor en batterij, ook als je ze niet ziet.

   4. De pauzeknop (WCAG 2.2.2). Wat in een lus speelt of langer dan vijf
      seconden beweegt, moet de bezoeker kunnen stilzetten. Het label wisselt
      tussen Pauzeren en Afspelen; de vertaalde teksten staan als
      data-attribuut op de knop, met het Nederlands als vangnet.
      Een keuze van de bezoeker wint: wie pauzeert, krijgt de video niet
      terug bij het terugscrollen, en wie bij verminderde beweging zelf op
      Afspelen drukt, krijgt hem wel.

   5. De geluidsknop (optioneel). Een video blijft altijd gedempt starten:
      browsers laten alleen gedempte video vanzelf spelen. Staat er in het
      kader een knop met data-el-video-geluid, dan kan de bezoeker het geluid
      zelf aanzetten. Dat is een bewuste klik, dus de browser laat het toe.
      De knop toont de toestand (doorgestreepte luidspreker = geluid uit),
      het label wisselt tussen Geluid aan en Geluid uit. Verlaat de video het
      beeld, dan gaat het geluid weer uit: wie terugscrollt krijgt geen geluid
      zonder erom te vragen. Wie het geluid aanzet bij een gepauzeerde of
      stilstaande video (bijvoorbeeld bij verminderde beweging), start hem
      daarmee ook.

   muted en playsinline staan al in de markup, want iOS kijkt daarnaar
   voor het script draait. Hier worden ze nog eens gezet als vangnet voor
   een video[data-el-video] van elders.
   ========================================================================== */

(() => {
  /* Elke mozaïeksectie zet dit script op de pagina, en een gewoon script
     draait bij elke tag opnieuw. Twee keer draaien geeft twee klik-
     listeners per knop: pauzeren en meteen weer afspelen. Eén keer is
     genoeg; nieuwe secties komen binnen via shopify:section:load. */
  if (window.elVideoGeladen) return;
  window.elVideoGeladen = true;

  const selector = 'video.ck__video, video[data-el-video]';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Per video: staat hij in beeld, en wat heeft de bezoeker zelf gekozen.
     keuze blijft null zolang er niet op de knop gedrukt is. */
  const toestand = new WeakMap();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const t = toestand.get(entry.target);
        if (!t) return;
        t.zichtbaar = entry.isIntersecting;
        stilBuitenBeeld(entry.target);
        bijwerken(entry.target);
      });
    },
    { threshold: 0.1 }
  );

  /* Buiten beeld gaat het geluid weer uit, zodat het niet zonder aanleiding
     terugkomt als de bezoeker later terugscrollt. */
  function stilBuitenBeeld(video) {
    const t = toestand.get(video);
    if (t && t.metGeluid && !t.zichtbaar && !video.muted) video.muted = true;
  }

  function magSpelen(video) {
    const t = toestand.get(video);
    if (!t || !t.zichtbaar) return false;
    if (t.keuze) return t.keuze === 'spelen';
    return !reduced.matches;
  }

  function bijwerken(video) {
    if (magSpelen(video)) proberenSpelen(video);
    else if (!video.paused) video.pause();
  }

  /* play() geeft een promise die afgewezen wordt als de browser het
     afspelen blokkeert. Zonder catch levert dat een onnodige fout in de
     console. */
  function proberenSpelen(video) {
    const poging = video.play();
    if (poging && typeof poging.catch === 'function') {
      poging.catch(() => {});
    }
  }

  function knopVan(video) {
    return video.closest('[data-el-video-kader]')?.querySelector('[data-el-video-knop]') ?? null;
  }

  /* Het label volgt wat de video echt doet, niet wat er gevraagd werd.
     Blokkeert de browser het afspelen (iOS in energiebesparing), dan staat
     er Afspelen en kan de bezoeker hem zelf starten. */
  function knopBijwerken(video) {
    const knop = knopVan(video);
    if (!knop) return;

    const speelt = !video.paused;
    const label = speelt
      ? knop.dataset.labelPauzeren || 'Pauzeren'
      : knop.dataset.labelAfspelen || 'Afspelen';

    if (knop.getAttribute('aria-label') !== label) knop.setAttribute('aria-label', label);
    knop.dataset.staat = speelt ? 'speelt' : 'gepauzeerd';
  }

  function geluidKnopVan(video) {
    return video.closest('[data-el-video-kader]')?.querySelector('[data-el-video-geluid]') ?? null;
  }

  /* Het label volgt de echte toestand van de video, niet de klik. */
  function geluidKnopBijwerken(video) {
    const knop = geluidKnopVan(video);
    if (!knop) return;

    const aan = !video.muted && video.volume > 0;
    const label = aan
      ? knop.dataset.labelUit || 'Geluid uit'
      : knop.dataset.labelAan || 'Geluid aan';

    if (knop.getAttribute('aria-label') !== label) knop.setAttribute('aria-label', label);
    knop.dataset.staat = aan ? 'aan' : 'uit';
  }

  function voorbereiden(video) {
    if (toestand.has(video)) return;
    toestand.set(video, { zichtbaar: false, keuze: null, metGeluid: false });

    video.muted = true;
    video.playsInline = true;

    // Of en wanneer hij speelt, beslist dit script
    video.autoplay = false;
    video.removeAttribute('autoplay');

    video.addEventListener('play', () => knopBijwerken(video));
    video.addEventListener('pause', () => knopBijwerken(video));

    // De kaart (of de bannersectie met data-el-video-kader) verbergt het
    // poster-beeld pas als er echt beeld is
    video.addEventListener('playing', () => {
      video.closest('.ck, [data-el-video-kader]')?.setAttribute('data-video-gestart', '');
    });

    const knop = knopVan(video);
    if (knop) {
      knop.addEventListener('click', (event) => {
        /* De knop staat naast de link, niet erin. Dit is een vangnet voor
           het geval hij ooit toch binnen een link belandt. */
        event.preventDefault();
        event.stopPropagation();

        const t = toestand.get(video);
        if (video.paused) {
          t.keuze = 'spelen';
          proberenSpelen(video);
        } else {
          t.keuze = 'pauze';
          video.pause();
        }
      });

      knopBijwerken(video);
      knop.hidden = false;
    }

    const geluid = geluidKnopVan(video);
    if (geluid) {
      toestand.get(video).metGeluid = true;

      video.addEventListener('volumechange', () => geluidKnopBijwerken(video));

      geluid.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const t = toestand.get(video);
        if (video.muted || video.volume === 0) {
          video.muted = false;
          if (video.volume === 0) video.volume = 1;

          // Een klik op geluid is ook een keuze om te spelen
          t.keuze = 'spelen';
          const poging = video.play();
          if (poging && typeof poging.catch === 'function') {
            // Blokkeert de browser het toch, dan terug naar stil
            poging.catch(() => { video.muted = true; });
          }
        } else {
          video.muted = true;
        }
      });

      geluidKnopBijwerken(video);
      geluid.hidden = false;
    }

    observer.observe(video);
  }

  function alles() {
    document.querySelectorAll(selector).forEach(voorbereiden);
  }

  /* Reageert als iemand verminderde beweging aan of uit zet zonder te
     herladen. De nieuwe voorkeur gaat voor een eerdere klik. */
  reduced.addEventListener('change', () => {
    document.querySelectorAll(selector).forEach((video) => {
      const t = toestand.get(video);
      if (!t) return;
      t.keuze = null;
      if (reduced.matches) video.closest('.ck, [data-el-video-kader]')?.removeAttribute('data-video-gestart');
      bijwerken(video);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', alles);
  } else {
    alles();
  }

  /* Video's die later in de pagina komen, bijvoorbeeld een marketing-tegel in
     het productraster bij oneindig scrollen of na het kiezen van een filter.
     Het thema zet die erbij zonder dat er een gebeurtenis voor is. Alleen de
     toegevoegde elementen worden bekeken, dus dit kost niets op een pagina
     zonder video. */
  new MutationObserver((mutaties) => {
    const nieuw = mutaties.some((m) =>
      Array.from(m.addedNodes).some(
        (n) => n.nodeType === 1 && (n.matches(selector) || n.querySelector(selector))
      )
    );
    if (nieuw) alles();
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Theme editor: een sectie hertekenen levert nieuwe video-elementen op
  document.addEventListener('shopify:section:load', alles);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(selector).forEach((video) => observer.unobserve(video));
  });
})();
